import React, { useEffect, useRef, useState, useMemo } from 'react'
import { ViewPropTypes, StyleSheet, View } from 'react-native'
import PropTypes from 'prop-types'

import Spinner from '../Spinner'
import Icon from '../Icons'
import Txt from '../Txt'
import MobileSelectInput from './index.js'
import { isMobileDevice } from '../../tools/platform'
import { useTheme } from '../../theming/ThemeProvider'
import Identity from '../../utils/Identity'
import { SelectInputStyledComponent } from './styles'
import MultiselectInput from './MultiselectInput'
import { usePlatforms } from '../../hooks/usePlatforms'

export default function SelectInput (props) {
  const {
    name, label, selected, keyProp, nameProp, getOptionLabel, onChange, icon, readonly,
    options, placeholder, multiselect, loading, filterAsync, containerStyle, error, loadMore, noMoreOptions, globalLoading
  } = props

  const selectRef = useRef(null)

  const { isTablet } = usePlatforms()
  const { theme } = useTheme()

  useEffect(() => {
    const select = selectRef.current
    if (select) {
      select.setState({
        ...select.state,
        isLoading: false,
        loadedOptions: options,
        passEmptyOptions: false
      })
    }
  }, [globalLoading, options])

  const [borderStyle, setBorderStyle] = useState(Identity.Borders(theme).Normal)

  useEffect(() => {
    setBorderStyle(Identity.Borders(theme)[error ? 'Error' : 'Normal'])
  }, [error, selected, theme])

  const customStyles = useMemo(() => ({
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontFamily: 'roboto-regular',
      fontSize: 12
    }),
    container: (provided) => ({
      ...provided,
      fontFamily: 'roboto-regular',
      fontSize: 12,
      height: 40,
      background: theme[readonly ? 'DISABLED_INPUT_COLOR' : 'WHITE'],
      borderRadius: 5
    }),
    control: (provided) => ({
      ...provided,
      ...borderStyle,
      boxShadow: 'none',
      background: 'transparent',
      height: '100%'
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: 'block',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      scrollbarWidth: 'none',
      marginLeft: icon ? 39 : 10,
      paddingLeft: 0,
      ...Identity.Font(isTablet).body2,
      lineHeight: '20px'
    }),
    multiValue: (provided) => ({
      ...provided,
      display: 'inline-flex'
    }),
    input: (provided) => ({
      ...provided,
      display: 'inline-flex',
      minWidth: 40
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme.BUNKER
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.WHITE
    }),
    menuList: (provided) => ({
      ...provided,
      borderRadius: 4
    }),
    option: (provided, state) => ({
      ...provided,
      color: theme[state.isFocused ? 'BUNKER_TEXT' : state.isSelected ? 'WHITE' : 'BUNKER'],
      backgroundColor: theme[state.isFocused ? 'GALLERY' : state.isSelected ? 'BUNKER' : 'WHITE']
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: 0
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      paddingLeft: 0
    })
  }), [borderStyle, icon, isTablet, theme, readonly])

  return isMobileDevice()
    ? <MobileSelectInput {...props} />
    : multiselect ? <MultiselectInput {...props} /> : (
      <View>
        {label ? (
          <Txt
            text={label}
            identity='body3Bold'
            style={styles.label}
          />) : null}
        <View style={[styles.container, containerStyle]}>
          <SelectInputStyledComponent
            ref={selectRef}
            className='react-select'
            classNamePrefix='react-select'
            loadingMessage={() => <Spinner />}
            styles={customStyles}
            value={selected}
            placeholder={placeholder}
            onChange={(newSelected) => onChange(name, newSelected)}
            defaultOptions={filterAsync ? options : null}
            options={!filterAsync ? options : null}
            isMulti={multiselect}
            isSearchable
            getOptionLabel={option => getOptionLabel ? getOptionLabel(option) : option[nameProp]}
            getOptionValue={option => option[keyProp]}
            closeMenuOnSelect={!multiselect}
            loadOptions={filterAsync}
            menuPortalTarget={document.body}
            portalPlacement='auto'
            position='fixed'
            isClearable
            isDisabled={readonly}
            handleLoadMore={!noMoreOptions ? loadMore : () => null}
            globalLoading={false}
            loading={loading}
          />
          <View style={styles.iconContainer}>
            <Icon
              name={icon}
              height={16}
              width={16}
              fill={theme.ICON_COLOR}
            />
          </View>
          {Boolean(error) &&
            <Txt
              text={error}
              identity='body3'
              numberOfLines={1}
              style={{
                margin: 0,
                marginTop: 3,
                color: theme.RED_ORANGE,
                position: 'absolute',
                left: 0,
                bottom: 1
              }}
            />}
        </View>
      </View>
    )
}

const styles = StyleSheet.create({
  iconContainer: {
    position: 'absolute',
    top: 12,
    left: 16
  },
  label: {
    marginBottom: 10
  },
  container: {
    height: 60
  }
})

SelectInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  selected: PropTypes.any,
  keyProp: PropTypes.string,
  nameProp: PropTypes.string,
  placeholder: PropTypes.string,
  icon: PropTypes.string,
  options: PropTypes.array,
  style: ViewPropTypes.style,
  containerStyle: ViewPropTypes.style,
  onChange: PropTypes.func,
  multiselect: PropTypes.bool,
  loading: PropTypes.bool,
  filterAsync: PropTypes.func,
  loadMore: PropTypes.func,
  getOptionLabel: PropTypes.func,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]),
  readonly: PropTypes.bool,
  noMoreOptions: PropTypes.bool,
  globalLoading: PropTypes.bool
}

SelectInput.defaultProps = {
  name: '',
  label: '',
  selected: null,
  keyProp: '',
  nameProp: '',
  placeholder: '',
  icon: '',
  options: [],
  style: null,
  onChange: () => {},
  multiselect: false,
  loading: false,
  filterAsync: null,
  getOptionLabel: null,
  error: false,
  loadMore: null,
  noMoreOptions: true
}
